import React, { useMemo, useRef, useEffect } from 'react'
import styled from 'styled-components'
import ReactECharts from 'echarts-for-react'
import { Carousel, Empty, Image } from 'antd'
import EmptyImg from '@/assets/imges/Empty/Empty.png'
import 'swiper/swiper-bundle.css'
import 'swiper/components/pagination/pagination.less'
import { IconFont } from '@/components'
import interrupt from '@/assets/imges/仪表盘任务中断状态.png'
import * as echarts from 'echarts'
import { t } from '@/i18n'

interface PropsType {
  showDeviceChart: boolean
  taskStatus: number
  taskScore: number | string
  failRate: {
    interceptSuccess: string
    protectionFailure: string
    detectionSuccess: string
  }
  protectedRate: {
    items: {
      attackTypeId: string
      attackTypeName: string
      rate: number
    }[]
  }
  riskRate: {
    riskLevel: string
    protectionFailureCount: number
    detectionSuccessCount: number
    interceptSuccessCount: number
  }[]
  resourceRate: {
    resourceLevel: string
    defenceFailCount: number
    detectionSuccessCount: number
    interceptionSuccessCount: number
  }[]
  templateRate: {
    templateName: string
    interceptionSuccessCount: number
    detectionSuccessCount: number
    defenceFailCount: number
    defenceFailRate: number
    detectionSuccessRate: number
    interceptionSuccessRate: number
  }[]
  deviceRate: {
    deviceName: string
    deviceIp: string
    detectionRate: number
    interceptionRate: number
  }[]
}

const TaskCharts: React.FC<PropsType> = ({
  showDeviceChart,
  taskStatus,
  taskScore,
  failRate,
  protectedRate,
  riskRate,
  resourceRate,
  templateRate,
  deviceRate,
}) => {
  const chunkSize = 5
  const protectedRateRef = useRef()
  const templateFailedChartRef = useRef<any>(null)
  const templateSuccessChartRef = useRef<any>(null)
  const score = useMemo(() => taskScore, [taskScore])
  const fail = useMemo(() => failRate, [failRate])
  const protect = useMemo(() => {
    let result: { attackTypeId: string; attackTypeName: string; rate: number }[][] = []
    if (protectedRate?.items) {
      for (let i = 0; i < protectedRate?.items.length; i += chunkSize) {
        result.push(protectedRate?.items?.slice(i, i + chunkSize))
      }
    }

    return result
  }, [protectedRate])

  const templateFailedRateData = useMemo(
    () =>
      templateRate?.map(item => ({
        defenceFailCount: item.defenceFailCount,
        defenceFailRate: item.defenceFailRate / 100,
      })),
    [templateRate],
  )

  const deviceRateData = useMemo(() => deviceRate, [deviceRate])

  // 连接模板图表，实现同步滚动
  useEffect(() => {
    if (templateFailedChartRef.current && templateSuccessChartRef.current) {
      const failedChart = templateFailedChartRef.current.getEchartsInstance()
      const successChart = templateSuccessChartRef.current.getEchartsInstance()

      if (failedChart && successChart) {
        echarts.connect('templateChartGroup')
        failedChart.group = 'templateChartGroup'
        successChart.group = 'templateChartGroup'
      }
    }
  }, [templateRate])

  const scoreOption = (values: { value: number; title: string }) => {
    const { value, title } = values
    return {
      series: [
        {
          type: 'gauge',
          min: 0,
          max: 100,
          splitNumber: 5,
          radius: '100%',
          axisLine: {
            lineStyle: {
              width: 12,
              color: [
                [0, '#B70001'],
                [0.2, '#B70001'],
                [0.4, '#FF2F56'],
                [0.6, '#FFA70B'],
                [0.8, '#74A1FA'],
                [1, '#67C23A'],
              ],
            },
          },
          pointer: {
            icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
            length: '15%',
            width: 10,
            offsetCenter: [0, -45],
            itemStyle: {
              color: 'inherit',
            },
          },
          axisTick: {
            length: 5,
            lineStyle: {
              color: 'inherit',
              width: 1,
            },
            distance: 3,
          },
          splitLine: {
            length: 8,
            distance: 2,
            lineStyle: {
              color: 'inherit',
              width: 2,
            },
          },
          axisLabel: {
            color: '#666666',
            fontSize: 8,
            distance: 15,
          },
          title: {
            offsetCenter: [0, 20],
            fontSize: 16,
            color: 'inherit',
          },
          detail: {
            fontSize: 30,
            offsetCenter: [0, -20],
            valueAnimation: true,
            formatter: function (num) {
              return `${Math.round(num)}`
            },
            color: 'inherit',
          },
          data: [
            {
              value: value,
              name: title,
            },
          ],
        },
      ],
    }
  }

  const failRateOption = values => ({
    tooltip: {
      trigger: 'item',
    },
    legend: {
      bottom: '10',
      left: 'center',
      selectedMode: false,
    },
    title: {
      text: t('防护失效'),
      subtext: `${fail?.protectionFailure || 0}%`,
      left: 'center',
      top: '35%',
      textStyle: {
        color: '#333',
        fontSize: 18,
        align: 'center',
      },
      subtextStyle: {
        color: '#333',
        fontSize: 25,
        fontWeight: 600,
      },
    },
    series: [
      {
        name: values && t('拦截状态'),
        type: 'pie',
        top: '-15%',
        radius: ['40%', '60%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
          position: 'center',
        },
        itemStyle: {
          normal: {
            color: function (colors) {
              let colorList = {
                拦截成功: '#5470C6',
                防护失效: '#EE6666',
                检测成功: '#88C56B',
              }
              return colorList[colors.name]
            },
          },
        },
        emphasis: {
          label: {
            show: false,
            fontSize: 18,
            fontWeight: 'bold',
          },
        },
        labelLine: {
          show: false,
        },
        data: values,
      },
    ],
  })

  const resourceFailedOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
    },
    grid: {
      left: '8%',
      right: '3%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      inverse: true,
      minInterval: 1,
    },
    yAxis: {
      type: 'category',
      position: 'right',
      data: [t('初级'), t('中级'), t('高级')],
      axisTick: {
        // 不要刻度线
        show: false,
      },
      axisLabel: {
        show: false,
      },
      inverse: true,
    },
    series: [
      {
        name: t('防护失效'),
        type: 'bar',
        stack: 'total',
        barWidth: 20,
        data: resourceRate?.map(item => item.defenceFailCount || 0),
        label: { show: false },
        itemStyle: { color: '#EE6666' },
      },
    ],
  })

  const resourceSuccessOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: { type: 'shadow' },
    },
    grid: {
      left: '3%',
      right: '6%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      minInterval: 1,
    },
    yAxis: {
      type: 'category',
      data: [t('初级'), t('中级'), t('高级')],
      axisTick: { show: false },
      axisLabel: {
        show: false,
        formatter: function (val) {
          if (val.length > 6) {
            val = `${val.substring(0, 6)}...`
          }
          return val
        },
      },
      inverse: true,
    },
    series: [
      {
        name: t('检测成功'),
        type: 'bar',
        stack: 'total',
        barWidth: 20,
        data: resourceRate?.map(item => item.detectionSuccessCount || 0),
        label: { show: false },
        itemStyle: { color: '#88C56B' },
      },
      {
        name: t('拦截成功'),
        type: 'bar',
        stack: 'total',
        barWidth: 20,
        data: resourceRate?.map(item => item.interceptionSuccessCount || 0),
        label: { show: false },
        itemStyle: { color: '#5470C6' },
      },
    ],
  })

  const riskFailedOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
    },
    grid: {
      left: '8%',
      right: '3%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      inverse: true,
      minInterval: 1,
    },
    yAxis: {
      type: 'category',
      position: 'right',
      data: [t('严重'), t('高危'), t('中危'), t('低危')],
      axisTick: {
        // 不要刻度线
        show: false,
      },
      axisLabel: {
        show: false,
      },
      inverse: true,
    },
    series: [
      {
        name: t('防护失效'),
        type: 'bar',
        stack: 'total',
        barWidth: 20,
        data: riskRate?.map(item => item.protectionFailureCount || 0),
        itemStyle: {
          color: '#EE6666',
        },
      },
    ],
  })

  const riskSuccessOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: { type: 'shadow' },
    },
    grid: {
      left: '3%',
      right: '6%',
      bottom: '10px',
      top: '10px',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      minInterval: 1,
    },
    yAxis: {
      type: 'category',
      data: [t('严重'), t('高危'), t('中危'), t('低危')],
      axisTick: { show: false },
      axisLabel: {
        show: false,
        formatter: function (val) {
          if (val.length > 6) {
            val = `${val.substring(0, 6)}...`
          }
          return val
        },
      },
      inverse: true,
    },
    series: [
      {
        name: t('检测成功'),
        type: 'bar',
        stack: 'total',
        barWidth: 20,
        data: riskRate?.map(item => item.detectionSuccessCount || 0),
        label: { show: false },
        itemStyle: { color: '#88C56B' },
      },
      {
        name: t('拦截成功'),
        type: 'bar',
        stack: 'total',
        barWidth: 20,
        data: riskRate?.map(item => item.interceptSuccessCount || 0),
        label: { show: false },
        itemStyle: { color: '#5470C6' },
      },
    ],
  })

  const templateFailedOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
    },
    grid: {
      left: '8%',
      right: '3%',
      bottom: '10px',
      top: 0,
      containLabel: true,
    },
    dataZoom: [
      {
        id: 'templateDataZoom',
        type: 'inside',
        yAxisIndex: 0,
        start: 0,
        end: 5,
        height: 20,
        bottom: 0,
        showDetail: false,
        moveHandleSize: 0,
        preventDefaultMouseMove: true,
        filterMode: 'none',
        rangeMode: ['value', 'value'],
        throttle: 100,
        minValueSpan: 5,
        maxValueSpan: 5,
      },
    ],

    xAxis: {
      type: 'value',
      inverse: true,
      minInterval: 1,
    },
    yAxis: {
      type: 'category',
      position: 'right',
      data: templateRate?.map(item => item.templateName),
      axisTick: {
        // 不要刻度线
        show: false,
      },
      axisLabel: {
        show: true,
        formatter: function (val) {
          if (val.length > 6) {
            return `${val.substring(0, 6)}...`
          }
          return val
        },
      },
      inverse: true,
    },
    series: {
      type: 'bar',
      barWidth: 12,
      data: templateFailedRateData?.map(item => item.defenceFailCount),
      label: {
        show: false,
        position: 'left',
      },
      itemStyle: {
        color: '#EE6666',
      },
      tooltip: {
        formatter: function (params) {
          return `
            <div>
              <div>
                <span>${t('防护失效数：')}</span>
                <span>${
                  templateFailedRateData?.find(item => item.defenceFailCount === params.value)
                    ?.defenceFailCount || 0
                }</span>
              </div>
              <div>
                <span>${t('防护失效率：')}</span>
                <span>${
                  templateFailedRateData?.find(item => item.defenceFailCount === params.value)
                    ?.defenceFailRate || 0
                }%</span>
              </div>
               <div>
               <span style="color: #EE6666;">●</span>
                <span>${params.name}</span>
              </div>
            </div>
          `
        },
      },
    },
  })

  const templateSuccessOption = () => ({
    tooltip: {
      trigger: 'item',
      confine: true,
      axisPointer: { type: 'shadow' },
    },
    grid: {
      left: '3%',
      right: '6%',
      bottom: '10px',
      top: 0,
      containLabel: true,
    },
    dataZoom: [
      {
        id: 'templateDataZoom',
        type: 'inside',
        yAxisIndex: 0,
        start: 0,
        end: 5,
        height: 20,
        bottom: 0,
        showDetail: false,
        moveHandleSize: 0,
        preventDefaultMouseMove: true,
        filterMode: 'none',
        rangeMode: ['value', 'value'],
        throttle: 100,
        minValueSpan: 5,
        maxValueSpan: 5,
      },
    ],

    xAxis: {
      type: 'value',
      minInterval: 1,
    },
    yAxis: {
      type: 'category',
      data: templateRate?.map(item => item.templateName),
      axisTick: { show: false },
      axisLabel: {
        show: false,
        formatter: function (val) {
          if (val.length > 6) {
            return (val = `${val.substring(0, 6)}...`)
          }
          return val
        },
      },
      inverse: true,
    },
    series: [
      {
        name: t('检测成功'),
        type: 'bar',
        stack: 'total',
        barWidth: '12px',
        data: templateRate?.map(item => item.detectionSuccessCount || 0),
        label: { show: false },
        itemStyle: { color: '#88C56B' },
        tooltip: {
          formatter: function (params) {
            return `
              <div>
                <div>
                  <span>${t('检测成功数：')}</span>
                  <span>${
                    templateRate?.find(item => item.templateName === params.name)
                      ?.detectionSuccessCount || 0
                  }</span>
                </div>
                <div>
                  <span>${t('检测成功率：')}</span>
                  <span>${
                    (templateRate?.find(item => item.templateName === params.name)
                      ?.detectionSuccessRate || 0) / 100
                  }%</span>
                </div>
                 <div>
                  <span style="color: #88C56B;">●</span>
                  <span>${params.name}</span>
                </div>
              </div>
            `
          },
        },
      },
      {
        name: t('拦截成功'),
        type: 'bar',
        stack: 'total',
        barWidth: '12px',
        data: templateRate?.map(item => item.interceptionSuccessCount || 0),
        label: { show: false },
        itemStyle: { color: '#5470C6' },
        tooltip: {
          formatter: function (params) {
            return `
              <div>
                <div>
                  <span>${t('拦截成功数：')}</span>
                  <span>${
                    templateRate?.find(item => item.templateName === params.name)
                      ?.interceptionSuccessCount || 0
                  }</span>
                </div>
                <div>
                  <span>${t('拦截成功率：')}</span>
                  <span>${
                    (templateRate?.find(item => item.templateName === params.name)
                      ?.interceptionSuccessRate || 0) / 100
                  }%</span>
                </div>
                 <div>
                  <span style="color: #5470C6;">●</span>
                  <span>${params.name}</span>
                </div>
              </div>
            `
          },
        },
      },
    ],
  })

  const deviceOption = () => ({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        let tar = params[0]
        let tar2 = params[1]
        return `${tar.name}<br/>
          <span style="color:#F44F4F;">●</span> ${t('拦截率')}: ${tar.value}%<br/>
          <span style="color:#88C56B;">●</span> ${t('检测率')}: ${tar2.value}%`
      },
    },
    legend: {
      data: [t('拦截率'), t('检测率')],
      bottom: 10,
      left: 'center',
      itemWidth: 18,
      itemHeight: 10,
      textStyle: { fontSize: 14, color: '#666' },
      icon: 'rect',
    },
    grid: {
      left: 0,
      right: 10,
      top: 30,
      bottom: 50,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: {
        formatter: '{value}%',
        color: '#999',
        fontSize: 13,
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'category',
      data: deviceRateData?.map(item => `${item.deviceName || '-'}`),
      axisTick: { show: false },
      axisLabel: {
        show: true,
        fontSize: 12,
        color: '#86909C',
        align: 'left',
        margin: 70,
        formatter: function (val) {
          if (val.length > 8) {
            val = `${val.substring(0, 8)}...`
          }
          return val
        },
      },
    },
    series: [
      {
        name: t('拦截率'),
        type: 'bar',
        stack: 'total',
        barWidth: 12,
        itemStyle: { color: '#F44F4F' },
        data: deviceRateData?.map(item => item.interceptionRate / 100),
        label: { show: false },
        z: 2,
      },
      {
        name: t('检测率'),
        type: 'bar',
        stack: 'total',
        barWidth: 12,
        itemStyle: { color: '#88C56B' },
        data: deviceRateData?.map(item => item.detectionRate / 100),
        label: { show: false },
        z: 1,
      },
    ],
  })

  return (
    <ChartContainer>
      <div className="top">
        <div className="left">
          <div className="task-score-wrapper">
            {taskStatus === 4 || score === '-' ? (
              <div className="task-score-wrapper">
                <Image src={interrupt} style={{ marginBottom: '40px' }} />
              </div>
            ) : (
              <div className="task-score">
                <ReactECharts
                  style={{ height: '180px' }}
                  option={scoreOption({
                    value: Number(score) / 100,
                    title:
                      (Number(score) / 100 <= 100 && Number(score) / 100 >= 80 && t('优秀')) ||
                      (Number(score) / 100 < 80 && Number(score) / 100 >= 60 && t('良好')) ||
                      (Number(score) / 100 < 60 && Number(score) / 100 >= 40 && t('一般')) ||
                      (Number(score) / 100 < 40 && Number(score) / 100 >= 20 && t('较弱')) ||
                      t('弱'),
                  })}
                />
              </div>
            )}
            <div className="tag">{t('安全评分')}</div>
          </div>
          <div className="fail-rate">
            <ReactECharts
              style={{ height: '240px' }}
              option={failRateOption(
                [
                  Number(fail.interceptSuccess) > 0
                    ? {
                        value: Number(fail.interceptSuccess),
                        name: t('拦截成功'),
                      }
                    : null,
                  Number(fail?.protectionFailure) > 0
                    ? {
                        value: Number(fail?.protectionFailure),
                        name: t('防护失效'),
                      }
                    : null,
                  Number(fail?.detectionSuccess) > 0
                    ? {
                        value: Number(fail.detectionSuccess),
                        name: t('检测成功'),
                      }
                    : null,
                ].filter(Boolean),
              )}
            />
          </div>
        </div>
        <div className="right">
          <div className="protected-rate">
            <span className="title">{t('攻击防护排名')}</span>
            <span className="sub-title">{t('各攻击类型拦截率排名')}</span>
            {protect?.length > 0 ? (
              <div className="carousel">
                <Carousel ref={protectedRateRef} dots={false} autoplay>
                  {protect.map((item, index) => (
                    <div className="carousel-item" key={index}>
                      {item.map((v, i) => (
                        <ItemChild key={i} index={i}>
                          <div>
                            <span className="tag">{index * chunkSize + i + 1}</span>
                            <span>{v.attackTypeName}</span>
                          </div>
                          <div>
                            <span className="rate">{v.rate / 100 || 0}%</span>
                          </div>
                        </ItemChild>
                      ))}
                    </div>
                  ))}
                </Carousel>
                {protect?.length > 1 && (
                  <span
                    className="prev-btn-wrapper"
                    onClick={() => {
                      if (protectedRateRef.current) {
                        protectedRateRef.current.prev()
                      }
                    }}
                  >
                    <IconFont type="icon-fanhui2" className="prev-btn" />
                  </span>
                )}
                {protect?.length > 1 && (
                  <span
                    className="next-btn-wrapper"
                    onClick={() => {
                      if (protectedRateRef.current) {
                        protectedRateRef.current.next()
                      }
                    }}
                  >
                    <IconFont type="icon-gengduo2" className="next-btn" />
                  </span>
                )}
              </div>
            ) : (
              <Empty image={EmptyImg} imageStyle={{ marginTop: '40px' }} />
            )}
          </div>
        </div>
      </div>
      <div className="center">
        <div className="left">
          <div className="resource-level">
            <span
              style={{
                fontSize: '14px',
                marginLeft: '3px',
                fontWeight: 500,
              }}
            >
              {t('用例等级分布')}
            </span>
            <div style={{ display: 'flex' }}>
              <div style={{ width: '45%' }}>
                <ReactECharts style={{ height: 190 }} option={resourceFailedOption()} />
              </div>
              <div className="labels">
                <div>{t('初级')}</div>
                <div>{t('中级')}</div>
                <div>{t('高级')}</div>
              </div>
              <div style={{ width: '45%' }}>
                <ReactECharts style={{ height: 190 }} option={resourceSuccessOption()} />
              </div>
            </div>
            <div className="legends">
              <div className="legend">
                <div className="tag red" />
                <div>{t('防护失效')}</div>
              </div>
              <div className="legend">
                <div className="tag blue" />
                <div>{t('拦截成功')}</div>
              </div>
              {showDeviceChart && (
                <div className="legend">
                  <div className="tag green" />
                  <div>{t('检测成功')}</div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="right">
          <div className="risk-level">
            <span
              style={{
                fontSize: '14px',
                marginLeft: '3px',
                fontWeight: 500,
              }}
            >
              {t('危险等级分布')}
            </span>
            <div style={{ display: 'flex' }}>
              <div style={{ width: '45%' }}>
                <ReactECharts style={{ height: 190 }} option={riskFailedOption()} />
              </div>
              <div className="labels">
                <div>{t('严重')}</div>
                <div>{t('高危')}</div>
                <div>{t('中危')}</div>
                <div>{t('低危')}</div>
              </div>
              <div style={{ width: '45%' }}>
                <ReactECharts style={{ height: 190 }} option={riskSuccessOption()} />
              </div>
            </div>
            <div className="legends">
              <div className="legend">
                <div className="tag red" />
                <div>{t('防护失效')}</div>
              </div>
              <div className="legend">
                <div className="tag blue" />
                <div>{t('拦截成功')}</div>
              </div>
              {showDeviceChart && (
                <div className="legend">
                  <div className="tag green" />
                  <div>{t('检测成功')}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bottom">
        <div className="left">
          <div className="template-fail-rate">
            <span className="title" style={{ fontWeight: 500 }}>
              {t('模板防护失效率排名')}
            </span>
            <div style={{ display: 'flex' }}>
              <div style={{ width: '50%' }}>
                <ReactECharts
                  ref={templateFailedChartRef}
                  style={{ height: 190 }}
                  option={templateFailedOption()}
                />
              </div>
              <div style={{ width: '40%' }}>
                <ReactECharts
                  ref={templateSuccessChartRef}
                  style={{ height: 190 }}
                  option={templateSuccessOption()}
                />
              </div>
            </div>
            <div className="legends">
              <div className="legend">
                <div className="tag red" />
                <div>{t('防护失效')}</div>
              </div>
              <div className="legend">
                <div className="tag blue" />
                <div>{t('拦截成功')}</div>
              </div>
              {showDeviceChart && (
                <div className="legend">
                  <div className="tag green" />
                  <div>{t('检测成功')}</div>
                </div>
              )}
            </div>
          </div>
        </div>
        {showDeviceChart && (
          <div className="right">
            <div className="device-protection-rank">
              <span
                style={{
                  fontSize: '14px',
                  marginLeft: '3px',
                  fontWeight: 500,
                }}
              >
                {t('设备防护能力排名')}
              </span>
              {showDeviceChart ? (
                <ReactECharts style={{ height: 240 }} option={deviceOption()} />
              ) : (
                <Empty image={EmptyImg} imageStyle={{ marginTop: '40px' }} />
              )}
            </div>
          </div>
        )}
      </div>
    </ChartContainer>
  )
}

export default TaskCharts

const ChartContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  .carousel-item {
    padding: 0 2px;
  }

  .top {
    width: 100%;
    min-height: 245px;
    margin-bottom: 30px;
    display: flex;

    .left {
      width: 50%;
      display: flex;
      min-width: 676px;

      .task-score-wrapper {
        width: 49%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-top: 10px;

        .task-score {
          width: 100%;
          height: 100%;
          margin-bottom: 16px;
        }

        .tag {
          width: 92px;
          height: 24px;
          background: rgba(115, 160, 250, 0.1);
          border-radius: 4px;
          border: 1px solid #73a0fa;
          color: #73a0fa;
          font-size: 14px;
          line-height: 22px;
          text-align: center;
        }
      }

      .fail-rate {
        width: 49%;
        height: 100%;
        margin-left: 20px;
      }
    }

    .right {
      width: 49%;
      display: flex;
      justify-content: space-between;

      .protected-rate {
        width: 100%;
        height: 246px;
        background: #f8f9fb;
        border-radius: 8px;
        padding: 12px 10px 12px 16px;

        .title {
          font-size: 14px;
          font-weight: 500;
          margin-right: 8px;
        }

        .sub-title {
          font-size: 12px;
          color: #666;
        }

        .carousel {
          position: relative;
          padding: 0 10px;
          height: 100%;

          .prev-btn-wrapper {
            position: absolute;
            top: 50%;
            left: -10px;
            transform: translateY(-50%);
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #d9d9d9;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .prev-btn {
            font-size: 12px;
            color: #fff;

            &:hover {
              cursor: pointer;
            }
          }

          .next-btn-wrapper {
            position: absolute;
            top: 50%;
            right: -5px;
            transform: translateY(-50%);
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #d9d9d9;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .next-btn {
            font-size: 12px;
            color: #fff;

            &:hover {
              cursor: pointer;
            }
          }
        }
      }
    }
  }

  .center {
    width: 100%;
    height: 260px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .left {
      width: 49%;
      min-width: 676px;
      height: 100%;
      background: #f8f9fb;
      border-radius: 8px;
      padding: 10px 16px 0 16px;
      overflow-y: scroll;

      .resource-level {
        width: 100%;
        background: #f8f9fb;
        border-radius: 8px;

        .legends {
          display: flex;
          justify-content: center;

          .legend {
            font-size: 12px;
            display: flex;
            margin: 10px 12px;
            align-items: center;
            color: #333333;

            .tag {
              width: 18px;
              height: 12px;
              border-radius: 2px;
              margin-right: 5px;
            }

            div {
              line-height: 12px;
            }

            .red {
              background: #ee6666;
            }

            .blue {
              background: #5470c6;
            }

            .green {
              background: #88c56b;
            }

            .pink {
              background: #f6eef2;
            }
          }
        }

        .labels {
          div {
            color: #989ba3;
            font-size: 12px;
            margin-top: 32px;
          }
        }
      }
    }

    .right {
      width: 49%;
      height: 100%;
      min-width: 676px;
      background: #f8f9fb;
      border-radius: 8px;
      padding: 10px 16px 0 16px;

      .risk-level {
        width: 100%;
        background: #f8f9fb;
        border-radius: 8px;

        .legends {
          display: flex;
          justify-content: center;

          .legend {
            font-size: 12px;
            display: flex;
            margin: 10px 12px;
            align-items: center;
            color: #333333;

            .tag {
              width: 18px;
              height: 12px;
              border-radius: 2px;
              margin-right: 5px;
            }

            div {
              line-height: 12px;
            }

            .red {
              background: #ee6666;
            }

            .blue {
              background: #5470c6;
            }

            .green {
              background: #88c56b;
            }

            .pink {
              background: #f6eef2;
            }
          }
        }

        .labels {
          div {
            color: #989ba3;
            font-size: 12px;
            margin-top: 24px;
          }
        }
      }
    }
  }

  .bottom {
    width: 100%;
    height: 260px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .left {
      width: 49%;
      min-width: 676px;
      height: 100%;
      background: #f8f9fb;
      border-radius: 8px;
      padding: 10px 16px 0 16px;
      overflow-y: scroll;

      .template-fail-rate {
        width: 100%;
        background: #f8f9fb;
        border-radius: 8px;

        .legends {
          display: flex;
          justify-content: center;

          .legend {
            font-size: 12px;
            display: flex;
            margin: 10px 12px;
            align-items: center;
            color: #333333;

            .tag {
              width: 18px;
              height: 12px;
              border-radius: 2px;
              margin-right: 5px;
            }

            div {
              line-height: 12px;
            }

            .red {
              background: #ee6666;
            }

            .blue {
              background: #5470c6;
            }

            .green {
              background: #88c56b;
            }

            .pink {
              background: #f6eef2;
            }
          }
        }

        .labels {
          width: 60px;
          padding-top: 20px;

          .ant-typography {
            margin-top: 16px;
          }
        }
      }
    }

    .right {
      width: 49%;
      height: 100%;
      min-width: 676px;
      background: #f8f9fb;
      border-radius: 8px;
      padding: 10px 16px 0 16px;

      .device-protection-rank {
        width: 100%;
        background: #f8f9fb;
        border-radius: 8px;

        .legends {
          display: flex;
          justify-content: center;

          .legend {
            font-size: 12px;
            display: flex;
            margin: 10px 12px;
            align-items: center;
            color: #333333;

            .tag {
              width: 18px;
              height: 12px;
              border-radius: 2px;
              margin-right: 5px;
            }

            div {
              line-height: 12px;
            }

            .red {
              background: #ee6666;
            }

            .blue {
              background: #5470c6;
            }

            .green {
              background: #88c56b;
            }

            .pink {
              background: #f6eef2;
            }
          }
        }

        .labels {
          div {
            color: #989ba3;
            font-size: 12px;
            margin-top: 24px;
          }
        }
      }
    }
  }
`

const ItemChild = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;

  .tag {
    display: inline-block;
    width: 14px;
    height: 14px;
    background: ${({ index }) =>
      index === 0
        ? 'linear-gradient(180deg, #fe6e2f 0%, #fe3101 100%)'
        : index === 1
        ? 'linear-gradient(180deg, #f34f50 0%, #f31700 100%)'
        : index === 2
        ? 'linear-gradient( 180deg, #FED837 0%, #FEB107 100%)'
        : '#8D94A8'};
    border-radius: 4px;
    font-size: 12px;
    color: #fff;
    line-height: 14px;
    text-align: center;
    margin-right: 6px;
  }

  .rate {
    color: #f5222d;
    margin-right: 5px;
  }
`
